/**
 * Hero layout variants (NOS-152). `centered` remains HeroSection (untouched
 * default). Variants share the hero's content fields — switching never loses
 * content — and are token-driven: palette, radius, and elevation come from the
 * --nos-* variables injected at the site root.
 *
 * Note: element_order/_el_styles are centered-hero features; variants define
 * their own composition and intentionally ignore them.
 */
import { Button } from "@/components/ui/button"
import type { Organization } from "@/templates/types"
import { resolveHeroMinHeight, type BlockComponentProps } from "./shared"

interface HeroContent {
  headline: string
  subheadline: string
  ctaText: string
  ctaUrl: string
  backgroundImage?: string
  backgroundImageAlt: string
  overlayOpacity: number
  logoHidden: boolean
  headlineDeleted: boolean
  subheadlineDeleted: boolean
  ctaDeleted: boolean
}

function parseHeroContent(content: Record<string, unknown> | null, org?: Organization | null): HeroContent {
  return {
    headline: (content?.headline as string) ?? org?.name ?? "Your impact starts here",
    subheadline:
      (content?.subheadline as string) ??
      (org?.mission_statement as string) ??
      "Empowering communities through sustainable solutions.",
    ctaText: (content?.cta_text as string) ?? "Donate now",
    ctaUrl: (content?.cta_url as string) || "#donate",
    backgroundImage: content?.background_image as string | undefined,
    backgroundImageAlt: (content?.background_image_alt as string) ?? "",
    overlayOpacity: Math.min(1, Math.max(0, (content?.background_overlay_opacity as number) ?? 0.55)),
    logoHidden: Boolean(content?.logo_hidden || content?.logo_deleted),
    headlineDeleted: Boolean(content?.headline_deleted || content?.headline_hidden),
    subheadlineDeleted: Boolean(content?.subheadline_deleted || content?.subheadline_hidden),
    ctaDeleted: Boolean(content?.cta_text_deleted || content?.cta_text_hidden),
  }
}

/** Accent falls back to secondary (resolveTokens defaults accent = secondary), so orgs without an accent keep the primary→secondary gradient. */
const GRADIENT = "linear-gradient(135deg, var(--nos-color-primary, #3b82f6) 0%, var(--nos-color-accent, var(--nos-color-secondary, #1e40af)) 100%)"

function PrimaryCta({ text, url, onPrimary = false }: { text: string; url: string; onPrimary?: boolean }) {
  const ctx = onPrimary ? "-on-primary" : ""
  return (
    <Button
      size="lg"
      className="min-h-[48px] min-w-[180px] px-8 text-base font-semibold shadow-lg transition-all hover:scale-105 hover:opacity-90 @sm:min-h-[52px] @sm:px-10 @sm:text-lg"
      style={{
        backgroundColor: `var(--nos-button${ctx}-bg, var(--nos-color-primary, #3b82f6))`,
        color: `var(--nos-button${ctx}-color, var(--nos-color-text-on-primary, #ffffff))`,
        borderWidth: 2,
        borderStyle: "solid",
        borderColor: `var(--nos-button${ctx}-border-color, transparent)`,
        borderRadius: "var(--nos-button-radius, 8px)",
      }}
      asChild
    >
      <a href={url}>{text}</a>
    </Button>
  )
}

/** Split: copy left on light surface, image (or gradient panel) right. */
export function HeroSplit({ content, org, inBuilder }: BlockComponentProps) {
  const c = parseHeroContent(content, org)
  return (
    <section
      className="grid items-center gap-8 px-4 @sm:px-6 @md:grid-cols-2 @md:gap-12 @md:px-8"
      style={{
        background: "var(--nos-color-surface, #ffffff)",
        paddingTop: "calc(var(--nos-space-section-y, 64px) * 1.25)",
        paddingBottom: "calc(var(--nos-space-section-y, 64px) * 1.25)",
        minHeight: resolveHeroMinHeight(content, inBuilder, undefined, 380),
      }}
    >
      <div className="flex max-w-xl flex-col items-start @md:justify-self-end">
        {!c.logoHidden && org?.logo_url && (
          <img src={org.logo_url} alt={org.name ?? "Organization logo"} className="mb-6 h-10 w-auto object-contain @md:h-12" />
        )}
        {!c.headlineDeleted && (
          <h1
            className="text-4xl font-bold tracking-tight @sm:text-5xl @md:text-6xl"
            style={{ color: "var(--nos-color-text, #0f172a)" }}
          >
            {c.headline}
          </h1>
        )}
        {!c.subheadlineDeleted && (
          <p className="mt-4 max-w-lg text-lg leading-relaxed @sm:text-xl" style={{ color: "var(--nos-color-text-muted, #64748b)" }}>
            {c.subheadline}
          </p>
        )}
        {!c.ctaDeleted && (
          <div className="mt-8">
            <PrimaryCta text={c.ctaText} url={c.ctaUrl} />
          </div>
        )}
      </div>
      {c.backgroundImage ? (
        <img
          src={c.backgroundImage}
          alt={c.backgroundImageAlt || ""}
          className="h-64 w-full object-cover @sm:h-80 @md:h-[28rem]"
          style={{ borderRadius: "var(--nos-radius, 12px)", boxShadow: "var(--nos-elevation-shadow, none)" }}
        />
      ) : (
        <div
          className="h-64 w-full @sm:h-80 @md:h-[28rem]"
          style={{ background: GRADIENT, borderRadius: "var(--nos-radius, 12px)", boxShadow: "var(--nos-elevation-shadow, none)" }}
          aria-hidden
        />
      )}
    </section>
  )
}

/** Full-bleed: cover image (or gradient), scrim from the bottom, copy bottom-left. */
export function HeroFullBleed({ content, org, inBuilder }: BlockComponentProps) {
  const c = parseHeroContent(content, org)
  return (
    <section
      className="relative flex flex-col justify-end px-4 @sm:px-6 @md:px-8"
      style={{
        ...(c.backgroundImage
          ? { backgroundImage: `url(${c.backgroundImage})`, backgroundSize: "cover", backgroundPosition: "center" }
          : { background: GRADIENT }),
        paddingTop: "calc(var(--nos-space-section-y, 64px) * 2)",
        paddingBottom: "calc(var(--nos-space-section-y, 64px) * 1.25)",
        minHeight: resolveHeroMinHeight(content, inBuilder, "85vh", 380),
      }}
      role={c.backgroundImage ? "img" : undefined}
      aria-label={c.backgroundImage ? c.backgroundImageAlt || "Hero section background" : undefined}
    >
      <div
        className="absolute inset-0"
        style={{
          background: c.backgroundImage
            ? `linear-gradient(to top, rgba(0,0,0,${Math.min(0.85, c.overlayOpacity + 0.2)}) 0%, rgba(0,0,0,0) 65%)`
            : "rgba(0,0,0,0.2)",
        }}
        aria-hidden
      />
      <div className="relative z-10 flex max-w-3xl flex-col items-start">
        {!c.logoHidden && org?.logo_url && (
          <img src={org.logo_url} alt={org.name ?? "Organization logo"} className="mb-5 h-10 w-auto object-contain drop-shadow-md @md:h-12" />
        )}
        {!c.headlineDeleted && (
          <h1 className="text-4xl font-bold tracking-tight text-white drop-shadow-lg @sm:text-5xl @md:text-6xl @lg:text-7xl">
            {c.headline}
          </h1>
        )}
        {!c.subheadlineDeleted && (
          <p className="mt-4 max-w-2xl text-lg leading-relaxed text-white/95 drop-shadow @sm:text-xl">
            {c.subheadline}
          </p>
        )}
        {!c.ctaDeleted && (
          <div className="mt-8">
            <PrimaryCta text={c.ctaText} url={c.ctaUrl} onPrimary />
          </div>
        )}
      </div>
    </section>
  )
}

/** Minimal: quiet editorial opening — no imagery, no logo, generous whitespace. */
export function HeroMinimal({ content, org, inBuilder }: BlockComponentProps) {
  const c = parseHeroContent(content, org)
  return (
    <section
      className="flex flex-col items-center px-4 text-center @sm:px-6 @md:px-8"
      style={{
        background: "var(--nos-color-surface, #ffffff)",
        paddingTop: "calc(var(--nos-space-section-y, 64px) * 1.5)",
        paddingBottom: "calc(var(--nos-space-section-y, 64px) * 1.5)",
        minHeight: resolveHeroMinHeight(content, inBuilder, undefined, 380),
      }}
    >
      <div className="flex max-w-3xl flex-col items-center">
        {!c.headlineDeleted && (
          <h1
            className="text-4xl font-bold tracking-tight @sm:text-5xl @md:text-6xl"
            style={{ color: "var(--nos-color-text, #0f172a)" }}
          >
            {c.headline}
          </h1>
        )}
        {!c.subheadlineDeleted && (
          <p className="mt-6 max-w-xl text-lg leading-relaxed @sm:text-xl" style={{ color: "var(--nos-color-text-muted, #64748b)" }}>
            {c.subheadline}
          </p>
        )}
        {!c.ctaDeleted && (
          <a
            href={c.ctaUrl}
            className="mt-10 text-base font-semibold underline decoration-2 underline-offset-8 transition-opacity hover:opacity-70 @sm:text-lg"
            style={{ color: "var(--nos-color-primary, #3b82f6)", textDecorationColor: "var(--nos-color-primary, #3b82f6)" }}
          >
            {c.ctaText} →
          </a>
        )}
      </div>
    </section>
  )
}

/**
 * NOS-236: Floating image (SaaS-style, July 2026 bench audit). Copy left on a
 * light surface, image floating right in an elevated card over a soft brand
 * blob. Without an image the blob alone carries the composition, so this
 * doubles as a background-image-optional hero layout.
 */
export function HeroFloating({ content, org, inBuilder }: BlockComponentProps) {
  const c = parseHeroContent(content, org)
  return (
    <section
      className="relative overflow-hidden px-4 @sm:px-6 @md:px-8"
      style={{
        background: "var(--nos-color-surface, #ffffff)",
        paddingTop: "calc(var(--nos-space-section-y, 64px) * 1.5)",
        paddingBottom: "calc(var(--nos-space-section-y, 64px) * 1.5)",
        minHeight: resolveHeroMinHeight(content, inBuilder, undefined, 380),
      }}
    >
      <div className="mx-auto grid max-w-6xl items-center gap-10 @md:grid-cols-[1.1fr_1fr] @md:gap-12">
        <div className="flex flex-col items-start">
          {!c.logoHidden && org?.logo_url && (
            <img src={org.logo_url} alt={org.name ?? "Organization logo"} className="mb-6 h-10 w-auto object-contain @md:h-12" />
          )}
          {!c.headlineDeleted && (
            <h1
              className="text-4xl font-bold tracking-tight @sm:text-5xl @md:text-6xl"
              style={{ color: "var(--nos-color-text, #0f172a)" }}
            >
              {c.headline}
            </h1>
          )}
          {!c.subheadlineDeleted && (
            <p className="mt-4 max-w-lg text-lg leading-relaxed @sm:text-xl" style={{ color: "var(--nos-color-text-muted, #64748b)" }}>
              {c.subheadline}
            </p>
          )}
          {!c.ctaDeleted && (
            <div className="mt-8">
              <PrimaryCta text={c.ctaText} url={c.ctaUrl} />
            </div>
          )}
        </div>
        <div className="relative">
          <div
            className="absolute -right-8 -top-8 h-3/4 w-3/4 rounded-full opacity-15 blur-2xl"
            style={{ background: GRADIENT }}
            aria-hidden
          />
          {c.backgroundImage ? (
            <img
              src={c.backgroundImage}
              alt={c.backgroundImageAlt || ""}
              className="relative h-64 w-full object-cover @sm:h-80 @md:h-96"
              style={{
                borderRadius: "calc(var(--nos-radius, 12px) * 1.5)",
                boxShadow: "var(--nos-elevation-shadow, 0 24px 48px -12px rgb(0 0 0 / 0.25))",
              }}
            />
          ) : (
            <div
              className="relative h-64 w-full @sm:h-80 @md:h-96"
              style={{
                background: GRADIENT,
                borderRadius: "calc(var(--nos-radius, 12px) * 1.5)",
                boxShadow: "var(--nos-elevation-shadow, 0 24px 48px -12px rgb(0 0 0 / 0.25))",
              }}
              aria-hidden
            />
          )}
        </div>
      </div>
    </section>
  )
}

/**
 * NOS-162: Video hero (research §C.1 — WaterAid/Eden/Obama Foundation
 * pattern). Background video, muted/looped/inline, over the poster image —
 * the poster (background_image) always paints first, so no-JS, reduced-motion
 * (CSS-hidden video), and slow networks all degrade to the full-bleed look.
 */
export function HeroVideo({ content, org, inBuilder }: BlockComponentProps) {
  const c = parseHeroContent(content, org)
  const videoUrl = (content?.video_url as string) ?? ""
  const showVideo = Boolean(videoUrl) && !inBuilder

  return (
    <section
      className="relative flex flex-col items-center justify-center overflow-hidden px-4 text-center @sm:px-6 @md:px-8"
      style={{
        ...(c.backgroundImage
          ? { backgroundImage: `url(${c.backgroundImage})`, backgroundSize: "cover", backgroundPosition: "center" }
          : { background: GRADIENT }),
        paddingTop: "calc(var(--nos-space-section-y, 64px) * 2)",
        paddingBottom: "calc(var(--nos-space-section-y, 64px) * 2)",
        minHeight: resolveHeroMinHeight(content, inBuilder, "85vh", 380),
      }}
      role={c.backgroundImage ? "img" : undefined}
      aria-label={c.backgroundImage ? c.backgroundImageAlt || "Hero section background" : undefined}
    >
      <style>{`@media (prefers-reduced-motion: reduce) { .nos-hero-video { display: none; } }`}</style>
      {showVideo && (
        <video
          className="nos-hero-video absolute inset-0 h-full w-full object-cover"
          src={videoUrl}
          poster={c.backgroundImage}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          aria-hidden="true"
          tabIndex={-1}
        />
      )}
      <div className="absolute inset-0" style={{ background: `rgba(0,0,0,${c.overlayOpacity})` }} aria-hidden />
      <div className="relative z-10 flex max-w-3xl flex-col items-center">
        {!c.logoHidden && org?.logo_url && (
          <img src={org.logo_url} alt={org.name ?? "Organization logo"} className="mb-5 h-10 w-auto object-contain drop-shadow-md @md:h-12" />
        )}
        {!c.headlineDeleted && (
          <h1 className="text-4xl font-bold tracking-tight text-white drop-shadow-lg @sm:text-5xl @md:text-6xl @lg:text-7xl">
            {c.headline}
          </h1>
        )}
        {!c.subheadlineDeleted && (
          <p className="mt-4 max-w-2xl text-lg leading-relaxed text-white/95 drop-shadow @sm:text-xl">
            {c.subheadline}
          </p>
        )}
        {!c.ctaDeleted && (
          <div className="mt-8">
            <PrimaryCta text={c.ctaText} url={c.ctaUrl} onPrimary />
          </div>
        )}
      </div>
    </section>
  )
}
